
import { useState, useEffect } from 'react';
import { usePersistentFormState } from '@/hooks/dashboard/usePersistentFormState';
import { AboutMeData, UseAboutMeStateProps } from './types/aboutMeTypes';
import { getYearsFromExperienceLevel, getExperienceLevelFromYears } from './utils/experienceUtils';
import { useAvatarState } from './hooks/useAvatarState';

export function useAboutMeState({
  initialBio = '',
  initialLocation = '',
  initialProfession = '',
  initialSkills = [],
  initialLanguages = [],
  initialEducation = '',
  initialYearsExperience = null,
  initialAvatarUrl = null,
  initialDisplayName = '',
  initialUsername = '',
  sectionId = 'about'
}: UseAboutMeStateProps) {
  // Use persistent form state for bio
  const [bioValue, setBioValue, resetBioValue] = usePersistentFormState(
    initialBio || '',
    'about_me_bio',
    sectionId
  );
  
  // Use persistent form state for location
  const [locationValue, setLocationValue, resetLocationValue] = usePersistentFormState(
    initialLocation || '',
    'about_me_location',
    sectionId
  );
  
  // Use persistent form state for profession
  const [professionValue, setProfessionValue, resetProfessionValue] = usePersistentFormState(
    initialProfession || '',
    'about_me_profession',
    sectionId
  );
  
  // Use persistent form state for skills
  const [skillsValue, setSkillsValue, resetSkillsValue] = usePersistentFormState<string[]>(
    initialSkills || [],
    'about_me_skills',
    sectionId
  );
  
  // Use persistent form state for languages
  const [languagesValue, setLanguagesValue, resetLanguagesValue] = usePersistentFormState<string[]>(
    initialLanguages || [],
    'about_me_languages',
    sectionId
  );
  
  // Use persistent form state for education
  const [educationValue, setEducationValue, resetEducationValue] = usePersistentFormState(
    initialEducation || '',
    'about_me_education',
    sectionId
  );
  
  // Use persistent form state for experience level
  const [experienceLevel, setExperienceLevel, resetExperienceLevel] = usePersistentFormState(
    getExperienceLevelFromYears(initialYearsExperience),
    'about_me_experience',
    sectionId
  );
  
  // Add persistent form states for display_name and username
  const [displayNameValue, setDisplayNameValue, resetDisplayNameValue] = usePersistentFormState(
    initialDisplayName || '',
    'about_me_display_name',
    sectionId
  );
  
  const [usernameValue, setUsernameValue, resetUsernameValue] = usePersistentFormState(
    initialUsername || '',
    'about_me_username',
    sectionId
  );
  
  const [isGeneratingBio, setIsGeneratingBio] = useState(false);

  // Use our custom avatar hook
  const {
    avatarValue,
    avatarFile,
    processedBlob,
    isUploadingAvatar,
    setAvatarValue,
    setAvatarFile,
    setProcessedBlob,
    setIsUploadingAvatar,
    handleAvatarChange
  } = useAvatarState(initialAvatarUrl);

  // Update local state when props change
  useEffect(() => {
    if (initialBio !== null && initialBio !== undefined && initialBio !== bioValue) {
      setBioValue(initialBio);
    }
    if (initialLocation !== null && initialLocation !== undefined && initialLocation !== locationValue) {
      setLocationValue(initialLocation);
    }
    if (initialProfession !== null && initialProfession !== undefined && initialProfession !== professionValue) {
      setProfessionValue(initialProfession);
    }
    if (initialSkills && JSON.stringify(initialSkills) !== JSON.stringify(skillsValue)) {
      setSkillsValue(initialSkills);
    }
    if (initialLanguages && JSON.stringify(initialLanguages) !== JSON.stringify(languagesValue)) {
      setLanguagesValue(initialLanguages);
    }
    if (initialEducation !== null && initialEducation !== undefined && initialEducation !== educationValue) {
      setEducationValue(initialEducation);
    }
    if (initialYearsExperience !== null && initialYearsExperience !== undefined && 
        getExperienceLevelFromYears(initialYearsExperience) !== experienceLevel) {
      setExperienceLevel(getExperienceLevelFromYears(initialYearsExperience));
    }
    if (initialAvatarUrl !== avatarValue) {
      setAvatarValue(initialAvatarUrl);
    }
    if (initialDisplayName !== null && initialDisplayName !== undefined && initialDisplayName !== displayNameValue) {
      setDisplayNameValue(initialDisplayName);
    }
    if (initialUsername !== null && initialUsername !== undefined && initialUsername !== usernameValue) {
      setUsernameValue(initialUsername);
    }
  }, [initialBio, initialLocation, initialProfession, initialSkills, initialLanguages, 
      initialEducation, initialYearsExperience, initialAvatarUrl, initialDisplayName, initialUsername]);

  // Reset all form fields
  const resetForm = () => {
    resetBioValue();
    resetLocationValue();
    resetProfessionValue();
    resetSkillsValue();
    resetLanguagesValue();
    resetEducationValue();
    resetExperienceLevel();
    resetDisplayNameValue();
    resetUsernameValue();
    setAvatarValue(initialAvatarUrl);
    setAvatarFile(null);
    setProcessedBlob(null);
  };

  const getData = (): AboutMeData => ({
    bio: bioValue,
    location: locationValue,
    profession: professionValue,
    skills: skillsValue,
    languages: languagesValue,
    education: educationValue,
    years_experience: experienceLevel ? getYearsFromExperienceLevel(experienceLevel) : null,
    avatar_url: avatarValue,
    display_name: displayNameValue,
    username: usernameValue
  });

  return {
    bioValue,
    locationValue,
    professionValue,
    skillsValue,
    languagesValue,
    educationValue,
    experienceLevel,
    isGeneratingBio,
    avatarValue,
    avatarFile,
    processedBlob,
    isUploadingAvatar,
    displayNameValue,
    usernameValue,
    setBioValue,
    setLocationValue,
    setProfessionValue,
    setSkillsValue,
    setLanguagesValue,
    setEducationValue,
    setExperienceLevel,
    setIsGeneratingBio,
    setAvatarValue,
    setAvatarFile,
    setProcessedBlob,
    setIsUploadingAvatar,
    setDisplayNameValue,
    setUsernameValue,
    handleAvatarChange,
    getYearsFromExperienceLevel,
    getExperienceLevelFromYears,
    resetForm,
    getData
  };
}
